import { ReactElement, Suspense, useEffect, useState } from 'react';
import {
  Avatar,
  Button,
  ContextMenu,
  ContextualTablePagination,
  FullLoader,
  Pill,
  TableColumns,
  TableContainer,
  TableRows,
  useTableContext,
} from '@sc/shared-ui';
import {
  CalendarIcon,
  EnvelopeIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { useMutation, useSuspenseQuery } from '@apollo/client/react';
import { useLocation, useRouter } from '@tanstack/react-router';
import { format } from 'date-fns';
import {
  CompanyUserRole,
  CompanyUserState,
  CompanyUserRowFragment,
  companyUserRoleDisplayMap,
  companyUserStateColourMap,
  companyUserStateDisplayMap,
  getCompanyUserState,
  getFragmentData,
} from '@sc/graphql';
import {
  GQL_FRAGMENT_COMPANY_USER_ROW,
  GQL_MUTATION_DELETE_COMPANY_USER,
  GQL_MUTATION_RESEND_INVITE,
  GQL_QUERY_INDEX_USERS,
} from '../utility/queries';
import { useAlerts } from '../providers/alerts';
import { notify } from '../utility/notify';
import { track } from '../utility/analytics';
import { EditUserModal } from '../modals/EditUserModal';
import { InviteUserModal } from '../modals/InviteUserModal';

type RowAction = 'delete' | 'edit' | 'resend';

const buildRowActions = (
  cu: CompanyUserRowFragment,
  state: CompanyUserState
): Array<{ label: string; value: RowAction }> => {
  if (cu.role === CompanyUserRole.accountOwner) return [];
  if (state === CompanyUserState.deleted) return [];
  if (state === CompanyUserState.joined) {
    return [
      { label: 'Edit', value: 'edit' },
      { label: 'Revoke access', value: 'delete' },
    ];
  }
  return [
    { label: 'Resend invite', value: 'resend' },
    { label: 'Delete', value: 'delete' },
  ];
};

const UsersTableRows = ({
  onEdit,
}: {
  onEdit: (cu: CompanyUserRowFragment) => void;
}): ReactElement => {
  const { setTotalPages, page, rowsPerPage } = useTableContext();
  const { useApolloError } = useAlerts();
  const onError = useApolloError();

  const { data } = useSuspenseQuery(GQL_QUERY_INDEX_USERS, {
    variables: {
      input: {
        filter: { state: null },
        pagination: { page, perPage: rowsPerPage },
      },
    },
  });

  useEffect(() => {
    setTotalPages(data.indexUsers.pagination.lastPage);
  }, [data, setTotalPages]);

  const [resendInvite] = useMutation(GQL_MUTATION_RESEND_INVITE);
  const [deleteCompanyUser] = useMutation(GQL_MUTATION_DELETE_COMPANY_USER);

  const items = getFragmentData(
    GQL_FRAGMENT_COMPANY_USER_ROW,
    data.indexUsers.items
  );

  return (
    <TableRows
      widthType="pc"
      rows={items.map((cu) => {
        const state = getCompanyUserState(cu);
        const actions = buildRowActions(cu, state);

        return {
          uuid: `${cu.userUuid}:${cu.companyUuid}`,
          cells: [
            {
              width: 24,
              content: cu.user.name ? (
                <div className="flex items-center gap-2">
                  <Avatar name={cu.user.name} fallback={cu.user.email} />
                  <span className="text-sm">
                    {cu.user.name}
                  </span>
                </div>
              ) : <></>,
            },
            {
              width: 24,
              content: (
                <>
                  <EnvelopeIcon className="size-5 mr-2 text-grey-400" />
                  <span className="text-sm">{cu.user.email}</span>
                </>
              ),
            },
            {
              width: 16,
              content: (
                <>
                  <CalendarIcon className="size-5 mr-2 text-grey-400" />
                  <span className="text-sm">
                    {cu.createdAt
                      ? format(new Date(cu.createdAt), 'd MMMM yyyy')
                      : '—'}
                  </span>
                </>
              ),
            },
            {
              width: 12,
              content: (
                <span className="text-sm">
                  {companyUserRoleDisplayMap[cu.role]}
                </span>
              ),
            },
            {
              width: 14,
              content: (
                <Pill
                  size="small"
                  colour={companyUserStateColourMap[state]}
                  text={companyUserStateDisplayMap[state]}
                />
              ),
            },
            {
              width: 10,
              wrapperClassname: 'justify-end',
              content: (
                <ContextMenu<RowAction>
                  size="medium"
                  type="basic"
                  disabled={actions.length === 0}
                  options={actions}
                  onOptionSelect={(action) => {
                    if (action === 'edit') {
                      onEdit(cu);
                      return;
                    }
                    if (action === 'resend') {
                      void resendInvite({
                        variables: {
                          input: { userUuid: cu.userUuid },
                        },
                        onCompleted: () => {
                          track('Invitation Resent');

                          notify.success('Invitation resent');
                        },
                        onError,
                      });
                      return;
                    }

                    void deleteCompanyUser({
                      variables: {
                        input: { userUuid: cu.userUuid },
                      },
                      optimisticResponse: { deleteUser: true },
                      update: (cache) => {
                        const id = cache.identify({
                          __typename: 'CompanyUser',
                          userUuid: cu.userUuid,
                          companyUuid: cu.companyUuid,
                        });

                        if (!id) return;
                        if (state === CompanyUserState.joined) {
                          cache.modify({
                            id,
                            fields: {
                              deletedAt: () => new Date().toISOString(),
                            },
                          });
                        } else {
                          cache.evict({ id });

                          cache.gc();
                        }
                      },
                      onCompleted: () => {
                        const wasJoined = state === CompanyUserState.joined;

                        track('User Access Removed', { wasJoined });

                        notify.success(
                          wasJoined ? 'Access revoked' : 'Invitation deleted'
                        );
                      },
                      onError,
                    });
                  }}
                />
              ),
            },
          ],
        };
      })}
    />
  );
};

const UsersTable = ({
  onEdit,
}: {
  onEdit: (cu: CompanyUserRowFragment) => void;
}): ReactElement => (
  <TableContainer>
    <TableColumns
      widthType="pc"
      columns={[
        { width: 24, heading: 'Name' },
        { width: 24, heading: 'Email' },
        { width: 16, heading: 'Joined' },
        { width: 12, heading: 'Role' },
        { width: 14, heading: 'State' },
        { width: 10 },
      ]}
    />
    <Suspense fallback={<FullLoader className="size-6" />}>
      <UsersTableRows onEdit={onEdit} />
    </Suspense>
    <ContextualTablePagination />
  </TableContainer>
);

export const Users = (): ReactElement => {
  const router = useRouter();
  const { hash } = useLocation();
  const [editing, setEditing] = useState<CompanyUserRowFragment | null>(null);

  useEffect(() => {
    if (hash === 'invite') track('Invite User Modal Viewed');
  }, [hash]);

  const closeInvite = () => void router.navigate({ to: '/users', hash: '' });

  return (
    <>
      <div className="p-5 flex items-center justify-between">
        <h1 className="text-h1-small font-semibold font-grotesque">Users</h1>
        <Button
          button={{
            text: 'Invite member',
            size: 'medium',
            shrink: true,
            IconEnd: PlusIcon,
            onClick: () =>
              void router.navigate({ to: '/users', hash: 'invite' }),
          }}
        />
      </div>
      <div className="pb-5 flex flex-col">
        <UsersTable onEdit={setEditing} />
      </div>
      <InviteUserModal open={hash === 'invite'} onClose={closeInvite} />
      <EditUserModal companyUser={editing} onClose={() => setEditing(null)} />
    </>
  );
};
